/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Store as StoreIcon, Link2, CheckCircle2, ShoppingBag, Globe, Copy, Mail, Eye, Code2, Sparkles } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { CHECKOUT_LANGUAGES } from '@/lib/checkoutI18n';

const newOptionId = () => `tmp_${Math.random().toString(36).slice(2, 10)}`;

const emptyForm = {
  name: '', platform: 'shopify', site_url: '', shopify_domain: '', admin_access_token: '', client_id: '', client_secret: '',
  woo_url: '', woo_consumer_key: '', woo_consumer_secret: '',
  auto_create_shopify_order: true,
  shipping_price: 0, shipping_name: 'Livraison standard', price_tolerance: 0,
  shipping_options: [],
  vitrine_id: '',
  display_name: '', logo_url: '', accent_color: '', support_email: '',
  slug: '', subdomain: '', custom_domains: [],
  meta_pixel_id: '', tiktok_pixel_id: '', google_tag_id: '', google_conversion_label: '',
  language: 'fr',
  currency: 'eur',
  stripe_receipt_enabled: false,
  whitelabel_receipt_enabled: true,
  stripe_payment_mode: 'element',
  token_capture_enabled: true,
  psp: 'stripe',
  payzen_account_id: '',
  payzen_payment_mode: 'embedded',
};

function ShippingOptionsEditor({ options, onChange }) {
  const update = (i, patch) => onChange(options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const remove = (i) => onChange(options.filter((_, idx) => idx !== i));
  const add = () => onChange([
    ...options,
    { id: newOptionId(), name: '', price: 0, description: '', image_url: '', is_default: options.length === 0 },
  ]);
  const setDefault = (i) => onChange(options.map((o, idx) => ({ ...o, is_default: idx === i })));

  return (
    <div className="space-y-3" data-testid="shipping-options-editor">
      {options.length === 0 && (
        <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md p-4 text-center">
          Aucune option configurée — ajoutez-en une pour permettre le paiement.
        </div>
      )}
      {options.map((o, i) => (
        <div key={o.id || i} className="border border-zinc-200 rounded-md p-3 space-y-3" data-testid={`shipping-option-row-${i}`}>
          <div className="grid grid-cols-[80px_1fr] gap-3">
            <ImageUpload
              value={o.image_url}
              onChange={(url) => update(i, { image_url: url })}
              aspectClass="h-20"
              testId={`shipping-image-${i}`}
            />
            <div className="space-y-2 min-w-0">
              <div className="grid grid-cols-[1fr_100px] gap-2">
                <Input
                  data-testid={`shipping-name-${i}`}
                  value={o.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                  className="h-9 rounded-md text-sm"
                  placeholder="Standard 3-5j ouvrés"
                />
                <Input
                  data-testid={`shipping-price-${i}`}
                  type="number"
                  step="0.01"
                  value={o.price}
                  onChange={(e) => update(i, { price: parseFloat(e.target.value) || 0 })}
                  className="h-9 rounded-md text-sm font-mono"
                  placeholder="€"
                />
              </div>
              <Input
                data-testid={`shipping-desc-${i}`}
                value={o.description}
                onChange={(e) => update(i, { description: e.target.value })}
                className="h-9 rounded-md text-xs"
                placeholder="Description (transporteur, délai, suivi…)"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name="shipping_default"
                    checked={!!o.is_default}
                    onChange={() => setDefault(i)}
                    data-testid={`shipping-default-${i}`}
                    className="cursor-pointer"
                  />
                  Par défaut (matching prix vitrine)
                </label>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`shipping-remove-${i}`}
                  className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" /> Retirer
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={add}
        data-testid="shipping-add-button"
        className="w-full h-9 rounded-md text-sm border-dashed"
      >
        <Plus className="h-3.5 w-3.5 mr-2" /> Ajouter une option
      </Button>
    </div>
  );
}

function StoreCard({ s, vitrines, onInstall, onCopyLink, onEdit, onRemove, selected, onToggleSelect }) {
  const isWoo = s.platform === 'woocommerce';
  const isSimple = s.platform === 'custom' || s.platform === 'ai';
  return (
    <Card className={`card-flat p-4 sm:p-6 transition-shadow ${selected ? 'ring-2 ring-zinc-900' : ''}`} data-testid={`store-card-${s.id}`}>
      <div className="flex flex-col gap-4">
        <div className="min-w-0 w-full">
          <div className="flex items-center gap-3 mb-1">
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect(s.id)}
              data-testid={`store-select-${s.id}`}
              className="h-4 w-4 shrink-0 accent-zinc-900 cursor-pointer"
              title="Sélectionner pour une action groupée"
            />
            {s.logo_url ? (
              <img src={s.logo_url} alt="" className="w-8 h-8 object-contain rounded border border-zinc-100" />
            ) : (
              <div className="w-8 h-8 rounded border border-zinc-100 flex items-center justify-center text-[10px] font-mono text-zinc-400">LOGO</div>
            )}
            <div className="font-display text-lg font-bold tracking-tight truncate">{s.name}</div>
          </div>
          <div className="text-xs text-zinc-500 font-mono truncate">{isSimple ? (s.site_url || '—') : isWoo ? s.woo_url : s.shopify_domain}</div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {isSimple ? (
              <span className={`px-2 py-0.5 rounded-md border font-mono inline-flex items-center gap-1 ${s.platform === 'ai' ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700' : 'bg-sky-50 border-sky-200 text-sky-700'}`} data-testid={`store-platform-badge-${s.id}`}>
                {s.platform === 'ai' ? <Sparkles className="h-3 w-3" /> : <Code2 className="h-3 w-3" />} {s.platform === 'ai' ? 'site IA' : 'site custom'} · sans back-office
              </span>
            ) : isWoo ? (
              s.woo_url ? (
                <span className="px-2 py-0.5 rounded-md border bg-violet-50 border-violet-200 text-violet-700 font-mono inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> woocommerce configuré
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-md border bg-amber-50 border-amber-200 text-amber-700 font-mono">
                  woocommerce non configuré
                </span>
              )
            ) : s.shopify_connected ? (
              <span className="px-2 py-0.5 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-700 font-mono inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> shopify connecté
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-md border bg-amber-50 border-amber-200 text-amber-700 font-mono">
                shopify non connecté
              </span>
            )}
            {!isSimple && <span className={`px-2 py-0.5 rounded-md border font-mono ${
              s.auto_create_shopify_order
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-zinc-100 border-zinc-200 text-zinc-600'
            }`}>
              {s.auto_create_shopify_order ? 'auto-order ON' : 'auto-order OFF'}
            </span>}
            <span className={`px-2 py-0.5 rounded-md border font-mono ${
              s.stripe_payment_mode === 'invoice'
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : s.stripe_payment_mode === 'link'
                  ? 'bg-sky-50 border-sky-200 text-sky-700'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-500'
            }`} data-testid={`store-mode-badge-${s.id}`}>
              {s.stripe_payment_mode === 'invoice' ? 'facture stripe' : s.stripe_payment_mode === 'link' ? 'lien paiement' : 'element'}
            </span>
            <span className={`px-2 py-0.5 rounded-md border ${
              s.token_capture_enabled === false ? 'bg-zinc-100 border-zinc-300 text-zinc-600' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
            }`} data-testid={`store-token-badge-${s.id}`}>
              {s.token_capture_enabled === false ? 'tokens OFF' : 'tokens ON'}
            </span>
            {s.psp === 'payzen' && (
              <span className="px-2 py-0.5 rounded-md border bg-indigo-50 border-indigo-200 text-indigo-700 font-mono" data-testid={`store-psp-badge-${s.id}`}>
                payzen · {s.payzen_payment_mode === 'redirect' ? 'redirection' : 'intégré'}
              </span>
            )}
            {s.vitrine_id && s.psp !== 'payzen' && (
              <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 text-zinc-700 font-mono">
                {vitrines.find((v) => v.id === s.vitrine_id)?.name || 'vitrine'}
              </span>
            )}
            {s.accent_color && (
              <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 flex items-center gap-1 font-mono">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.accent_color }} />
                {s.accent_color}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pt-3 border-t border-zinc-100">
          {!isWoo && !isSimple && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onInstall(s)}
              data-testid={`admin-store-install-${s.id}`}
              className="rounded-md text-xs"
              title={s.shopify_connected ? 'Reconnecter Shopify (régénère le token Admin)' : 'Installer l\'app Shopify (OAuth)'}
            >
              <Link2 className="h-3.5 w-3.5 mr-1.5" />
              {s.shopify_connected ? 'Reconnecter' : 'Installer Shopify'}
            </Button>
          )}
          {!isWoo && !isSimple && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCopyLink(s)}
              data-testid={`admin-store-copy-install-${s.id}`}
              className="rounded-md text-xs"
              title="Copier le lien d'installation OAuth pour l'envoyer à la personne qui a accès à la boutique"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copier le lien
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => onEdit(s)} data-testid={`admin-store-edit-${s.id}`} className="rounded-md">
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(s.id)}
            data-testid={ADMIN.deleteStoreButton}
            className="rounded-md text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [vitrines, setVitrines] = useState([]);
  const [payzenAccounts, setPayzenAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testingWoo, setTestingWoo] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState('fr');

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const wlOnCount = stores.filter((s) => s.whitelabel_receipt_enabled !== false).length;
  const previewUrl = `${BACKEND}/api/public/receipt-preview?store=${editing?.id || ''}&lang=${previewLang}`;

  const bulkReceipt = async (enabled) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post('/stores/receipts/whitelabel-bulk', { enabled });
      toast.success(
        enabled
          ? `Reçu de marque activé sur ${data.total} boutique(s)`
          : `Reçu de marque désactivé sur ${data.total} boutique(s)`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action groupée impossible');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const bulkPaymentMode = async (mode) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/stores/payment-mode/bulk', { store_ids: selectedIds, mode });
      const labels = { invoice: 'Facture Stripe', link: 'Lien de paiement', element: 'Element (intégré)' };
      toast.success(`Mode ${labels[mode]} appliqué sur ${data.updated} boutique(s)`);
      setSelectedIds([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action groupée impossible');
    } finally {
      setBulkBusy(false);
    }
  };

  const load = async () => {
    const [s, v, pz] = await Promise.all([api.get('/stores'), api.get('/vitrines'), api.get('/payzen-accounts').catch(() => ({ data: [] }))]);
    setStores(s.data);
    setVitrines(v.data);
    setPayzenAccounts(pz.data || []);
  };
  useEffect(() => { load(); }, []);

  // Detect ?shopify=connected query param after the OAuth callback redirect
  // and surface a success toast. The flag is then cleaned from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const platform = searchParams.get('platform') || '';
  useEffect(() => {
    if (searchParams.get('shopify') === 'connected') {
      toast.success('Shopify connecté — token Admin enregistré');
      const next = new URLSearchParams(searchParams);
      next.delete('shopify');
      next.delete('store');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const installShopify = async (store) => {
    if (!store.client_id) {
      toast.error('Renseigne d\'abord Client ID et Client Secret sur cette boutique');
      return;
    }
    try {
      const r = await api.get(`/shopify/oauth/start?store_id=${store.id}`);
      window.location.href = r.data.install_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de démarrer OAuth');
    }
  };

  // Copy the OAuth install URL to the clipboard instead of redirecting. Useful
  // when the operator doesn't have browser access to the merchant's Shopify
  // store: they can send this link to whoever can log into that store. The
  // OAuth `state` is stateless (encrypted store_id, no browser cookie), so the
  // callback connects the right store no matter who opens the link.
  const copyInstallLink = async (store) => {
    if (!store.client_id) {
      toast.error('Renseigne d\'abord Client ID et Client Secret sur cette boutique');
      return;
    }
    try {
      const r = await api.get(`/shopify/oauth/start?store_id=${store.id}`);
      const url = r.data.install_url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Lien d\'installation copié — envoie-le à la personne qui a accès à la boutique. Valable 24 h.');
      } catch {
        // Clipboard API blocked (insecure context / permissions): show the URL
        // via prompt so it can still be copied manually.
        window.prompt('Copie ce lien et envoie-le à la personne qui a accès à la boutique (valable 24 h) :', url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de générer le lien OAuth');
    }
  };

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, platform: ['woocommerce', 'custom', 'ai'].includes(platform) ? platform : 'shopify' }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...emptyForm, ...s, admin_access_token: '', client_secret: '', woo_consumer_secret: '' }); setOpen(true); };

  const isWoo = form.platform === 'woocommerce';
  const isSimple = form.platform === 'custom' || form.platform === 'ai';

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    if (isSimple) {
      if (form.site_url && !/^https?:\/\//i.test(form.site_url)) { toast.error('URL du site : commencez par https://'); return; }
    } else if (isWoo) {
      if (!form.woo_url) { toast.error('URL du site WordPress requise'); return; }
      if (!editing && (!form.woo_consumer_key || !form.woo_consumer_secret)) {
        toast.error('Consumer key et Consumer secret WooCommerce requis');
        return;
      }
    } else {
      if (!form.shopify_domain) { toast.error('Domaine Shopify requis'); return; }
      const hasStaticToken = !!form.admin_access_token;
      const hasClientCreds = !!form.client_id && !!form.client_secret;
      if (!editing && !hasStaticToken && !hasClientCreds) {
        toast.error('Fournissez soit un Admin API token (shpat_), soit Client ID + Client Secret');
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) {
        // Secrets left blank are preserved server-side (backend ignores empty
        // client_id/client_secret/admin_access_token to keep the stored value).
        await api.put(`/stores/${editing.id}`, form);
        toast.success('Boutique mise à jour');
      } else {
        await api.post('/stores', form);
        toast.success('Boutique créée');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer cette boutique ?')) return;
    await api.delete(`/stores/${id}`);
    toast.success('Boutique supprimée');
    await load();
  };

  const testWoo = async () => {
    if (!editing) return;
    setTestingWoo(true);
    try {
      const { data } = await api.post(`/stores/${editing.id}/test-woo`);
      toast.success(`Connexion WooCommerce OK (${data.products_total} produits)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connexion WooCommerce échouée');
    } finally {
      setTestingWoo(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="stores-page">
      <div className="flex flex-wrap items-end justify-between gap-5 mb-8">
        <div>
          <div className="dashboard-eyebrow">Configuration</div>
          <h1 data-testid="stores-title">Boutiques</h1>
          <p className="dashboard-caption">Shopify · WooCommerce · Sites customs · Sites IA</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-md" data-testid="bulk-receipt-menu-trigger" disabled={bulkBusy}>
                <Mail className="h-4 w-4 mr-2" /> Reçu de marque
                <span className="ml-2 text-xs text-zinc-400">{wlOnCount}/{stores.length}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Action groupée — toutes les boutiques</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="bulk-receipt-enable-all" onClick={() => bulkReceipt(true)}>
                <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" /> Activer sur toutes les boutiques
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="bulk-receipt-disable-all" onClick={() => bulkReceipt(false)}>
                <Trash2 className="h-4 w-4 mr-2 text-zinc-400" /> Désactiver sur toutes les boutiques
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid={ADMIN.addStoreButton} className="btn-primary rounded-md">
                <Plus className="h-4 w-4 mr-2" /> Ajouter une boutique
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? 'Modifier la boutique' : 'Nouvelle boutique'}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {isSimple ? 'Site sans back-office : branding white-label, vitrine de rattachement et checkout Gotham.' : isWoo ? 'Connexion WooCommerce, branding white-label et vitrine de rattachement.' : 'Identifiants Shopify, branding white-label et vitrine de rattachement.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">NOM</Label>
                <Input
                  value={form.name}
                  data-testid={ADMIN.storeNameInput}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Ma boutique principale"
                />
              </div>
              <div>
                <Label className="text-xs">PLATEFORME</Label>
                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[['shopify', 'Shopify'], ['woocommerce', 'WooCommerce'], ['custom', 'Site custom'], ['ai', 'Site IA']].map(([val, lbl]) => (
                    <button key={val} type="button"
                      data-testid={`store-platform-${val}`}
                      onClick={() => setForm({ ...form, platform: val })}
                      className={`h-10 rounded-md border text-sm font-medium transition-colors ${form.platform === val ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              {isSimple && (
                <div className="dashed-divider pt-4 space-y-3" data-testid="simple-site-fields">
                  <div className="overline mb-1">— {form.platform === 'ai' ? 'Site généré par IA' : 'Site custom (code maison)'}</div>
                  <div>
                    <Label className="text-xs">URL PUBLIQUE DU SITE (optionnel)</Label>
                    <Input value={form.site_url || ''} data-testid="store-site-url-input" onChange={(e) => setForm({ ...form, site_url: e.target.value })} className="mt-1.5 h-10 rounded-md font-mono" placeholder="https://monsite.com" />
                  </div>
                  <p className="text-xs text-zinc-500">Aucune connexion API : vous ajoutez vos produits ici, puis vous collez le snippet / les liens de paiement dans votre site. Les ventes restent enregistrées dans Gotham (Transactions, Clients, BatCycle) — aucune commande n'est créée ailleurs.</p>
                </div>
              )}
              {isWoo && (
                <div className="dashed-divider pt-4 space-y-3" data-testid="woo-fields">
                  <div className="overline mb-1">— Connexion WooCommerce (WordPress)</div>
                  <div>
                    <Label className="text-xs">URL DU SITE WORDPRESS</Label>
                    <Input value={form.woo_url} data-testid="store-woo-url-input"
                      onChange={(e) => setForm({ ...form, woo_url: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono" placeholder="https://maboutique.com" />
                  </div>
                  <div>
                    <Label className="text-xs">CONSUMER KEY (ck_...)</Label>
                    <Input value={form.woo_consumer_key} data-testid="store-woo-key-input"
                      onChange={(e) => setForm({ ...form, woo_consumer_key: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs" placeholder="ck_xxxxxxxxxxxx" />
                  </div>
                  <div>
                    <Label className="text-xs">CONSUMER SECRET (cs_...)</Label>
                    <Input value={form.woo_consumer_secret} type="password" data-testid="store-woo-secret-input"
                      onChange={(e) => setForm({ ...form, woo_consumer_secret: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder={editing ? '••• laisser vide pour ne pas changer' : 'cs_xxxxxxxxxxxx'} />
                  </div>
                  {editing && (
                    <Button type="button" variant="outline" size="sm" onClick={testWoo} disabled={testingWoo}
                      data-testid="store-woo-test-button">
                      {testingWoo ? 'Test…' : 'Tester la connexion'}
                    </Button>
                  )}
                  <p className="text-xs text-zinc-500">
                    WooCommerce → Réglages → Avancé → API REST → Créer une clé (permissions Lecture/Écriture).
                  </p>
                </div>
              )}
              {!isWoo && !isSimple && (
              <>
              <div>
                <Label className="text-xs">DOMAINE SHOPIFY</Label>
                <Input
                  value={form.shopify_domain}
                  data-testid={ADMIN.storeDomainInput}
                  onChange={(e) => setForm({ ...form, shopify_domain: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono"
                  placeholder="maboutique.myshopify.com"
                />
              </div>
              <div className="dashed-divider pt-4">
                <div className="overline mb-2">— Option A : Custom App (token statique)</div>
                <Label className="text-xs">ADMIN API ACCESS TOKEN (shpat_...)</Label>
                <Input
                  value={form.admin_access_token}
                  data-testid={ADMIN.storeTokenInput}
                  onChange={(e) => setForm({ ...form, admin_access_token: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono"
                  type="password"
                  placeholder={editing ? '••• laisser vide pour ne pas changer' : 'shpat_xxxxxxxx'}
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Pour les Custom Apps créées dans Settings → Apps → Develop apps
                </p>
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-2">— Option B : Dev Dashboard App (OAuth)</div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">CLIENT ID</Label>
                    <Input
                      value={form.client_id}
                      data-testid="admin-store-client-id-input"
                      onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder="abc123def456..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CLIENT SECRET</Label>
                    <Input
                      value={form.client_secret}
                      data-testid="admin-store-client-secret-input"
                      onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                      type="password"
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder={editing ? '••• laisser vide pour ne pas changer' : 'shpss_... ou autre'}
                    />
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Pour les apps créées dans le Dev Dashboard. Le token est échangé automatiquement via OAuth avant chaque appel.
                </p>
              </div>
              </>
              )}
              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Options de livraison</div>
                <p className="text-xs text-zinc-500 mb-3">
                  Le client choisira parmi ces options sur le checkout. Marquez l'option par défaut (utilisée pour matcher le prix vitrine).
                </p>
                <ShippingOptionsEditor
                  options={form.shipping_options}
                  onChange={(opts) => setForm({ ...form, shipping_options: opts })}
                />
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Matching (sync vitrine)</div>
                <Label className="text-xs">TOLÉRANCE PRIX (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price_tolerance}
                  data-testid="admin-store-tolerance-input"
                  onChange={(e) => setForm({ ...form, price_tolerance: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5 h-10 rounded-md font-mono max-w-[180px]"
                  placeholder="0.10"
                />
                <p className="text-xs text-zinc-500 mt-1">Écart toléré au matching · prix vitrine ≈ prix {isSimple ? 'du site' : isWoo ? 'WooCommerce' : 'Shopify'} + livraison par défaut ± tolérance</p>
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Prestataire de paiement</div>
                <div className="flex p-1 bg-zinc-100 rounded-lg w-fit" data-testid="admin-store-psp">
                  {[['stripe', 'Stripe / Viva (via vitrine)'], ['payzen', 'PayZen']].map(([k, lbl]) => (
                    <button key={k} type="button" data-testid={`admin-store-psp-${k}`}
                      onClick={() => setForm({ ...form, psp: k })}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${(form.psp || 'stripe') === k ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {(form.psp || 'stripe') === 'payzen' ? (
                  <div className="mt-3 space-y-3" data-testid="admin-store-payzen-block">
                    <div>
                      <Label className="text-xs">COMPTE PAYZEN</Label>
                      <select
                        data-testid="admin-store-payzen-account-select"
                        value={form.payzen_account_id || ''}
                        onChange={(e) => setForm({ ...form, payzen_account_id: e.target.value })}
                        className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                      >
                        <option value="">— Choisir un compte PayZen —</option>
                        {payzenAccounts.map((a) => (
                          <option key={a.id} value={a.id}>{a.name} · {a.environment === 'prod' ? 'LIVE' : 'TEST'}{a.configured ? '' : ' (incomplet)'}</option>
                        ))}
                      </select>
                      {payzenAccounts.length === 0 && <p className="text-xs text-amber-700 mt-1">Aucun compte PayZen : ajoute-le d'abord dans Configuration → Comptes PayZen.</p>}
                    </div>
                    <div>
                      <Label className="text-xs">MODE DE PAIEMENT PAYZEN</Label>
                      <div className="mt-1.5 flex p-1 bg-zinc-100 rounded-lg w-fit" data-testid="admin-store-payzen-mode">
                        {[['embedded', 'Formulaire intégré'], ['redirect', 'Page PayZen (redirection)']].map(([k, lbl]) => (
                          <button key={k} type="button" data-testid={`admin-store-payzen-mode-${k}`}
                            onClick={() => setForm({ ...form, payzen_payment_mode: k })}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${(form.payzen_payment_mode || 'embedded') === k ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-zinc-500 mt-1"><strong>Intégré</strong> : champs carte PayZen dans ton checkout. <strong>Redirection</strong> : page de paiement hébergée par PayZen puis retour sur la confirmation.</p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Label className="text-xs">SITE VITRINE LIÉ</Label>
                    <select
                      data-testid="admin-store-vitrine-select"
                      value={form.vitrine_id || ''}
                      onChange={(e) => setForm({ ...form, vitrine_id: e.target.value })}
                      className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                    >
                      <option value="">— Aucune (utilise le Stripe par défaut) —</option>
                      {vitrines.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-zinc-500 mt-1">Détermine quel compte Stripe (ou Viva) encaissera les paiements.</p>
                  </div>
                )}
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Branding checkout (white-label)</div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">NOM AFFICHÉ SUR LE CHECKOUT</Label>
                    <Input
                      data-testid="admin-store-display-name-input"
                      value={form.display_name}
                      onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                      className="mt-1.5 h-10 rounded-md"
                      placeholder={form.name || 'Ma Boutique'}
                    />
                    <p className="text-xs text-zinc-500 mt-1">Remplace "External Checkout" sur la page de paiement.</p>
                  </div>
                  <div>
                    <Label className="text-xs">LANGUE DU CHECKOUT</Label>
                    <select
                      data-testid="admin-store-language-select"
                      value={form.language || 'fr'}
                      onChange={(e) => setForm({ ...form, language: e.target.value })}
                      className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                    >
                      {CHECKOUT_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-zinc-500 mt-1">Traduit automatiquement la page de paiement (formulaire, livraison, récapitulatif) pour vos clients.</p>
                  </div>
                  <div>
                    <Label className="text-xs">DEVISE D'ENCAISSEMENT</Label>
                    <select
                      data-testid="admin-store-currency-select"
                      value={form.currency || 'eur'}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                    >
                      <option value="eur">EUR · Euro (€)</option>
                      <option value="usd">USD · Dollar US ($)</option>
                      <option value="gbp">GBP · Livre sterling (£)</option>
                      <option value="pln">PLN · Złoty polonais (zł)</option>
                      <option value="sek">SEK · Couronne suédoise (kr)</option>
                    </select>
                    <p className="text-xs text-zinc-500 mt-1">La devise est portée par la boutique : dans la Batmobile, chaque Stripe actif encaisse dans cette devise, peu importe la devise du compte Stripe.</p>
                  </div>
                  <div>
                    <ImageUpload
                      value={form.logo_url}
                      onChange={(url) => setForm({ ...form, logo_url: url })}
                      label="LOGO DE LA BOUTIQUE"
                      aspectClass="h-32"
                      testId="admin-store-logo-upload"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">COULEUR D'ACCENT</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input
                          type="color"
                          value={form.accent_color || '#1A1A1A'}
                          onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                          className="h-10 w-14 rounded-md p-1 cursor-pointer"
                        />
                        <Input
                          data-testid="admin-store-accent-input"
                          value={form.accent_color}
                          onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                          className="h-10 rounded-md font-mono text-xs"
                          placeholder="#1A1A1A"
                        />
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <Label className="text-xs font-bold flex items-center gap-1.5">
                        📧 EMAIL DE CONTACT CLIENT
                      </Label>
                      <p className="text-[10px] text-zinc-600 mt-1 mb-2">
                        Cet email apparaîtra sur la page de paiement pour que vos clients vous joignent en cas de question.
                      </p>
                      <Input
                        data-testid="admin-store-support-input"
                        type="email"
                        value={form.support_email}
                        onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                        className="h-10 rounded-md text-xs font-mono"
                        placeholder="contact@boutique.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashed-divider pt-4">
                <div className="overline mb-3">— Pixels marketing</div>
                <p className="text-xs text-zinc-500 mb-3">
                  Injectés automatiquement sur la landing campagne + checkout + page succès. Événements standards : <code className="font-mono bg-zinc-100 px-1 rounded text-[10px]">PageView</code> · <code className="font-mono bg-zinc-100 px-1 rounded text-[10px]">InitiateCheckout</code> · <code className="font-mono bg-zinc-100 px-1 rounded text-[10px]">Purchase</code>. Google : <code className="font-mono bg-zinc-100 px-1 rounded text-[10px]">begin_checkout</code> · <code className="font-mono bg-zinc-100 px-1 rounded text-[10px]">purchase</code> (+ conversion Ads si libellé).
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">META PIXEL ID</Label>
                    <Input
                      data-testid="admin-store-meta-pixel-input"
                      value={form.meta_pixel_id}
                      onChange={(e) => setForm({ ...form, meta_pixel_id: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder="1234567890"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">TIKTOK PIXEL ID</Label>
                    <Input
                      data-testid="admin-store-tiktok-pixel-input"
                      value={form.tiktok_pixel_id}
                      onChange={(e) => setForm({ ...form, tiktok_pixel_id: e.target.value })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder="C123ABC456..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">GOOGLE TAG ID</Label>
                    <Input
                      data-testid="admin-store-google-tag-input"
                      value={form.google_tag_id}
                      onChange={(e) => setForm({ ...form, google_tag_id: e.target.value.trim() })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder="AW-123456789 ou G-ABC123"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Google Ads (AW-…) ou GA4 (G-…). Charge gtag.js.</p>
                  </div>
                  <div>
                    <Label className="text-xs">LIBELLÉ DE CONVERSION ADS</Label>
                    <Input
                      data-testid="admin-store-google-label-input"
                      value={form.google_conversion_label}
                      onChange={(e) => setForm({ ...form, google_conversion_label: e.target.value.trim() })}
                      className="mt-1.5 h-10 rounded-md font-mono text-xs"
                      placeholder="AbC-D_efGhIjKlMnOp"
                      disabled={!form.google_tag_id}
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Optionnel · la partie après « / » dans <span className="font-mono">AW-123/XXXX</span>. Déclenche la conversion Achat.</p>
                  </div>
                </div>
              </div>

              {!isSimple && <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                <div>
                  <Label className="text-sm">
                    {isWoo ? 'Créer la commande sur WordPress (WooCommerce)' : 'Créer auto. la commande dans Shopify'}
                  </Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    {isWoo
                      ? 'Après chaque paiement réussi : crée la commande dans WooCommerce et redirige le client vers la boutique.'
                      : 'Après chaque paiement réussi'}
                  </p>
                </div>
                <Switch
                  checked={form.auto_create_shopify_order}
                  data-testid={ADMIN.storeAutoOrderSwitch}
                  onCheckedChange={(v) => setForm({ ...form, auto_create_shopify_order: v })}
                />
              </div>
              }

              {(form.psp || 'stripe') !== 'payzen' && <div className="border-t border-zinc-100 pt-4">
                <Label className="text-sm">Mode de paiement Stripe</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-3">
                  <strong>Element</strong> : formulaire de carte intégré (capture off-session possible). <strong>Facture Stripe</strong> : redirection vers une facture hébergée (facture officielle, aucun email Stripe). <strong>Lien de paiement</strong> : redirection vers une page Stripe Checkout unique qui expire en 30 min, puis retour sur la confirmation.
                </p>
                <div className="flex p-1 bg-zinc-100 rounded-lg w-fit" data-testid="admin-store-payment-mode">
                  {[['element', 'Element (intégré)'], ['invoice', 'Facture Stripe'], ['link', 'Lien de paiement']].map(([k, lbl]) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`admin-store-mode-${k}`}
                      onClick={() => setForm({ ...form, stripe_payment_mode: k })}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                        (form.stripe_payment_mode || 'element') === k ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>}

              <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                <div>
                  <Label className="text-sm">Enregistrement des cartes (tokens)</Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    Enregistre la carte off-session (annuaire Clients / BatCycle / Copy PAN) à chaque paiement CB réussi sur <strong>cette boutique</strong>, quel que soit le Stripe utilisé. Désactivez pour ne collecter aucun token sur cette boutique.
                  </p>
                </div>
                <Switch
                  checked={form.token_capture_enabled !== false}
                  data-testid="admin-store-token-capture-switch"
                  onCheckedChange={(v) => setForm({ ...form, token_capture_enabled: v })}
                />
              </div>

              <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                <div>
                  <Label className="text-sm">Reçu de marque (email white-label)</Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    Envoie automatiquement votre propre reçu de commande (logo, couleurs, nom, email de support) après chaque paiement carte réussi. Aucune mention Stripe. Activer ceci <strong>désactive le reçu Stripe</strong> pour éviter les doublons.
                  </p>
                  <button
                    type="button"
                    data-testid="open-receipt-preview"
                    onClick={() => setPreviewOpen(true)}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-900 hover:text-zinc-600 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" /> Aperçu du reçu
                  </button>
                </div>
                <Switch
                  checked={!!form.whitelabel_receipt_enabled}
                  data-testid="admin-store-whitelabel-receipt-switch"
                  onCheckedChange={(v) => setForm({ ...form, whitelabel_receipt_enabled: v, stripe_receipt_enabled: v ? false : form.stripe_receipt_enabled })}
                />
              </div>

              <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
                <div>
                  <Label className={`text-sm ${form.whitelabel_receipt_enabled ? 'text-zinc-400' : ''}`}>Reçu Stripe automatique (carte)</Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    {form.whitelabel_receipt_enabled
                      ? 'Désactivé automatiquement car le reçu de marque est activé.'
                      : 'Quand activé, Stripe envoie son reçu par email au client après chaque paiement carte réussi. Désactivé par défaut (aucun reçu envoyé).'}
                  </p>
                </div>
                <Switch
                  checked={!!form.stripe_receipt_enabled}
                  disabled={!!form.whitelabel_receipt_enabled}
                  data-testid="admin-store-stripe-receipt-switch"
                  onCheckedChange={(v) => setForm({ ...form, stripe_receipt_enabled: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid={ADMIN.saveStoreButton}
                className="btn-primary rounded-md"
              >
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* In-admin receipt preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Aperçu du reçu de marque</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Rendu réel avec le branding de {editing?.name ? <strong>{editing.name}</strong> : 'la boutique'} (logo, couleurs, nom, email de support).
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {['fr', 'en', 'es', 'nl', 'it', 'de', 'pl'].map((lg) => (
              <button
                key={lg}
                type="button"
                data-testid={`receipt-preview-lang-${lg}`}
                onClick={() => setPreviewLang(lg)}
                className={`h-8 px-3 rounded-md border text-xs font-medium uppercase transition-colors ${previewLang === lg ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'}`}
              >
                {lg}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-zinc-200 overflow-hidden bg-zinc-50" style={{ height: '520px' }}>
            <iframe
              key={previewUrl}
              src={previewUrl}
              title="Aperçu du reçu"
              data-testid="receipt-preview-iframe"
              className="w-full h-full border-0"
            />
          </div>
        </DialogContent>
      </Dialog>
      {(() => {
        const shopifyStores = stores.filter((s) => !['woocommerce', 'custom', 'ai'].includes(s.platform));
        const wooStores = stores.filter((s) => s.platform === 'woocommerce');
        const customStores = stores.filter((s) => s.platform === 'custom');
        const aiStores = stores.filter((s) => s.platform === 'ai');
        const visibleStores = platform === 'woocommerce' ? wooStores : platform === 'shopify' ? shopifyStores : platform === 'custom' ? customStores : platform === 'ai' ? aiStores : stores;
        const SimpleSection = ({ keyName, title, Icon, color, list, hint }) => (!platform || platform === keyName) && (
          <section data-testid={`stores-section-${keyName}`}>
            <div className="flex items-center gap-2.5 mb-4 pb-2 border-b border-zinc-100"><Icon className={`h-5 w-5 ${color}`} /><h2 className="font-display text-xl font-bold tracking-tight">{title}</h2><span className="text-xs font-mono text-zinc-400">({list.length})</span></div>
            {list.length === 0 ? <Card className="card-flat p-8 text-center"><Icon className="h-8 w-8 mx-auto text-zinc-300 mb-2" /><p className="text-zinc-500 text-sm">{hint}</p></Card>
              : <div className="grid md:grid-cols-2 gap-4">{list.map((s) => <StoreCard key={s.id} s={s} selected={selectedIds.includes(s.id)} {...cardProps} />)}</div>}
          </section>
        );
        const cardProps = { vitrines, onInstall: installShopify, onCopyLink: copyInstallLink, onEdit: openEdit, onRemove: remove, onToggleSelect: toggleSelect };
        return (
          <div className="space-y-10">
            {/* Bulk selection action bar (Stripe payment mode) */}
            <div className="sticky top-14 lg:top-2 z-20 -mb-4" data-testid="stores-bulk-bar">
              <Card className={`card-flat px-4 py-3 flex flex-wrap items-center gap-2 transition-all ${selectedIds.length ? 'ring-2 ring-zinc-900' : ''}`}>
                <span className="text-sm font-semibold" data-testid="stores-selected-count">
                  {selectedIds.length ? `${selectedIds.length} boutique(s) sélectionnée(s)` : 'Sélection groupée — cochez des boutiques ou'}
                </span>
                <Button
                  variant="outline" size="sm" className="h-8 rounded-md text-xs"
                  onClick={() => setSelectedIds(selectedIds.length === visibleStores.length ? [] : visibleStores.map((s) => s.id))}
                  data-testid="stores-select-all"
                >
                  {selectedIds.length === visibleStores.length && visibleStores.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
                </Button>
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Button
                      size="sm" className="h-8 rounded-md text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => bulkPaymentMode('invoice')} disabled={bulkBusy}
                      data-testid="stores-bulk-invoice"
                    >
                      → Facture Stripe
                    </Button>
                    <Button
                      size="sm" className="h-8 rounded-md text-xs bg-sky-600 hover:bg-sky-700 text-white"
                      onClick={() => bulkPaymentMode('link')} disabled={bulkBusy}
                      data-testid="stores-bulk-link"
                    >
                      → Lien de paiement
                    </Button>
                    <Button
                      variant="outline" size="sm" className="h-8 rounded-md text-xs"
                      onClick={() => bulkPaymentMode('element')} disabled={bulkBusy}
                      data-testid="stores-bulk-element"
                    >
                      → Element (intégré)
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-8 rounded-md text-xs text-zinc-500"
                      onClick={() => setSelectedIds([])}
                      data-testid="stores-bulk-clear"
                    >
                      Annuler
                    </Button>
                  </div>
                )}
              </Card>
            </div>

            {/* Section Shopify */}
            {(!platform || platform === 'shopify') && (
            <section data-testid="stores-section-shopify">
              <div className="flex items-center gap-2.5 mb-4 pb-2 border-b border-zinc-100">
                <ShoppingBag className="h-5 w-5 text-emerald-600" />
                <h2 className="font-display text-xl font-bold tracking-tight">Shopify</h2>
                <span className="text-xs font-mono text-zinc-400">({shopifyStores.length})</span>
              </div>
              {shopifyStores.length === 0 ? (
                <Card className="card-flat p-8 text-center">
                  <StoreIcon className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
                  <p className="text-zinc-500 text-sm">Aucune boutique Shopify. Ajoutez-en une via « Ajouter une boutique » (plateforme Shopify).</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {shopifyStores.map((s) => <StoreCard key={s.id} s={s} selected={selectedIds.includes(s.id)} {...cardProps} />)}
                </div>
              )}
            </section>
            )}

            {/* Section WordPress / WooCommerce */}
            {(!platform || platform === 'woocommerce') && (
            <section data-testid="stores-section-woocommerce">
              <div className="flex items-center gap-2.5 mb-4 pb-2 border-b border-zinc-100">
                <Globe className="h-5 w-5 text-violet-600" />
                <h2 className="font-display text-xl font-bold tracking-tight">WordPress / WooCommerce</h2>
                <span className="text-xs font-mono text-zinc-400">({wooStores.length})</span>
              </div>
              {wooStores.length === 0 ? (
                <Card className="card-flat p-8 text-center">
                  <Globe className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
                  <p className="text-zinc-500 text-sm">Aucune boutique WordPress. Ajoutez-en une via « Ajouter une boutique » (plateforme WooCommerce).</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {wooStores.map((s) => <StoreCard key={s.id} s={s} selected={selectedIds.includes(s.id)} {...cardProps} />)}
                </div>
              )}
            </section>
            )}
            <SimpleSection keyName="custom" title="Sites Customs" Icon={Code2} color="text-sky-600" list={customStores} hint="Aucun site custom. « Ajouter une boutique » → plateforme Site custom : vos produits et le checkout Gotham, sans back-office externe." />
            <SimpleSection keyName="ai" title="Sites IA" Icon={Sparkles} color="text-fuchsia-600" list={aiStores} hint="Aucun site IA. « Ajouter une boutique » → plateforme Site IA : branchez le checkout Gotham sur un site généré par IA." />
          </div>
        );
      })()}
    </div>
  );
}
